import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, CreditCard, Download, Plus, Pencil, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AccountShell } from "../../components/account/AccountShell";
import { useAuth } from "../../hooks/useAuth";
import {
  getSubscription,
  getPaymentMethods,
  getInvoices,
  getBillingInfo,
  downloadInvoicePdf,
} from "../../lib/api";
import { fmtDate, fmtMoney } from "../../lib/format";

const INCLUDED = [
  "1000+ Business & App Ideas",
  "Unlimited Idea Browsing",
  "AI Builder Prompts",
  "Business Idea Details",
  "Save Unlimited Favorites",
  "New Ideas Added Regularly",
  "Member-Only Content",
];

const Section = ({ title, children, testid, action }) => (
  <div
    className="bg-white rounded-2xl p-6 md:p-7 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
    data-testid={testid}
  >
    <div className="flex items-center justify-between gap-3 mb-4">
      <h2 className="font-display text-lg font-bold text-[#0F0F0F]">{title}</h2>
      {action}
    </div>
    {children}
  </div>
);

export default function AccountPlanDetails() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sub, setSub] = useState(null);
  const [cards, setCards] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [billing, setBilling] = useState({});
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([
      getSubscription().then((d) => d.subscription).catch(() => null),
      getPaymentMethods().catch(() => []),
      getInvoices().catch(() => []),
      getBillingInfo().catch(() => ({})),
    ])
      .then(([s, c, i, b]) => {
        setSub(s);
        setCards(c);
        setInvoices(i);
        setBilling(b || {});
      })
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const defaultCard = cards.find((c) => c.is_default) || cards[0];

  const download = async (inv) => {
    setDownloading(inv.id);
    try {
      await downloadInvoicePdf(inv.id, inv.invoice_number, inv.pdf_url);
    } catch {
      toast.error("Could not download invoice.");
    } finally {
      setDownloading(null);
    }
  };

  const statusLabel = sub
    ? sub.cancel_at_period_end
      ? "Cancelling"
      : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)
    : "No plan";

  return (
    <AccountShell title="Plans & Subscription" subtitle="Your membership, billing and receipts." testid="account-plan-details-page">
      <button
        onClick={() => navigate("/account/plans")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#687280] hover:text-[#0F0F0F] transition-colors mb-6"
        data-testid="back-to-plans-btn"
      >
        <ArrowLeft size={15} /> Back to Plans
      </button>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#687280]">
          <Loader2 className="animate-spin" size={22} />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Plan */}
          <Section title="Current Plan" testid="current-plan-section">
            {sub && (sub.status === "active" || sub.status === "cancelled") ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                <div>
                  <p className="text-xs text-[#9CA3AF]">Plan</p>
                  <p className="font-display text-xl font-bold text-[#0F0F0F] mt-0.5">{sub.plan_label}</p>
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF]">Status</p>
                  <span className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${sub.cancel_at_period_end ? "bg-amber-50 text-amber-600" : "bg-emerald-50 text-emerald-600"}`}>
                    {statusLabel}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF]">Price</p>
                  <p className="font-semibold text-[#0F0F0F] mt-0.5">
                    {fmtMoney(sub.amount, (sub.currency || "usd").toUpperCase())}/{sub.interval}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-[#9CA3AF]">{sub.cancel_at_period_end ? "Access ends" : "Next Billing Date"}</p>
                  <p className="font-semibold text-[#0F0F0F] mt-0.5">{fmtDate(sub.current_period_end)}</p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <p className="text-sm text-[#687280]">You don't have an active membership yet.</p>
                <button
                  onClick={() => navigate("/account/plans")}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
                  data-testid="details-upgrade-btn"
                >
                  View Plans
                </button>
              </div>
            )}
          </Section>

          {/* What's Included */}
          <Section title="What's Included" testid="whats-included-section">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-center gap-2.5 text-sm text-[#374151]">
                  <span className="w-5 h-5 rounded-full bg-[#D4AF37]/15 text-[#8a6d1a] flex items-center justify-center shrink-0">
                    <Check size={13} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </Section>

          {/* Payment Method */}
          <Section
            title="Payment Method"
            testid="payment-method-section"
            action={
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate("/account/payment-methods")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors"
                  data-testid="update-payment-method-btn"
                >
                  <Pencil size={13} /> Update
                </button>
                <button
                  onClick={() => navigate("/account/payment-methods")}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#0F0F0F] text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-[#1c1c1c] transition-colors"
                  data-testid="details-add-payment-method-btn"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
            }
          >
            {defaultCard ? (
              <div className="flex items-center gap-4">
                <div className="w-14 h-9 rounded-md bg-[#1a1f71] text-white text-[10px] font-bold flex items-center justify-center">
                  {defaultCard.brand.toUpperCase().slice(0, 4)}
                </div>
                <div>
                  <p className="font-semibold text-[#0F0F0F]">{defaultCard.brand} •••• {defaultCard.last4}</p>
                  <p className="text-xs text-[#687280]">
                    Expires {String(defaultCard.exp_month).padStart(2, "0")}/{String(defaultCard.exp_year).slice(-2)}
                  </p>
                </div>
                {defaultCard.is_default && (
                  <span className="ml-auto rounded-full bg-[#D4AF37]/15 text-[#8a6d1a] px-2.5 py-0.5 text-xs font-semibold">Default</span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-3 text-sm text-[#687280]">
                <CreditCard size={18} className="text-[#D4AF37]" />
                No payment method on file yet.
              </div>
            )}
          </Section>

          {/* Billing Information */}
          <Section
            title="Billing Information"
            testid="billing-information-section"
            action={
              <button
                onClick={() => navigate("/account/settings")}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors"
                data-testid="update-information-btn"
              >
                <Pencil size={13} /> Update Information
              </button>
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
              <div>
                <p className="text-xs text-[#9CA3AF]">Name</p>
                <p className="font-medium text-[#0F0F0F] mt-0.5">{billing.name || user?.name || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF]">Email</p>
                <p className="font-medium text-[#0F0F0F] mt-0.5">{billing.email || user?.email || "—"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-[#9CA3AF]">Billing Address</p>
                {billing.address_line1 ? (
                  <p className="font-medium text-[#0F0F0F] mt-0.5">
                    {billing.address_line1}
                    <br />
                    {[billing.city, billing.state, billing.country].filter(Boolean).join(", ")}
                    {billing.postal_code ? ` ${billing.postal_code}` : ""}
                  </p>
                ) : (
                  <p className="text-sm text-[#687280] mt-0.5">No billing address added.</p>
                )}
              </div>
            </div>
          </Section>

          {/* Invoice History */}
          <Section title="Invoice History" testid="invoice-history-section">
            {invoices.length === 0 ? (
              <p className="text-sm text-[#687280]">No invoices yet. Your receipts will appear here after a payment.</p>
            ) : (
              <div className="-mx-1">
                <div className="hidden sm:grid grid-cols-12 gap-4 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-[#9CA3AF] border-b border-gray-100">
                  <div className="col-span-3">Date</div>
                  <div className="col-span-3">Amount</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-3 text-right">Invoice</div>
                </div>
                {invoices.map((inv) => (
                  <div
                    key={inv.id}
                    className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-4 px-3 py-3.5 border-b border-gray-50 last:border-0 items-center"
                    data-testid={`details-invoice-row-${inv.id}`}
                  >
                    <div className="sm:col-span-3 text-sm font-medium text-[#0F0F0F]">{fmtDate(inv.date)}</div>
                    <div className="sm:col-span-3 text-sm font-semibold text-[#0F0F0F]">{fmtMoney(inv.amount, inv.currency)}</div>
                    <div className="sm:col-span-3">
                      <span className="inline-flex rounded-full bg-emerald-50 text-emerald-600 px-2.5 py-0.5 text-xs font-semibold">
                        {inv.status}
                      </span>
                    </div>
                    <div className="sm:col-span-3 sm:text-right">
                      <button
                        onClick={() => download(inv)}
                        disabled={downloading === inv.id}
                        className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-1.5 text-xs font-semibold text-[#0F0F0F] hover:border-[#D4AF37] transition-colors disabled:opacity-60"
                        data-testid={`details-download-invoice-${inv.id}`}
                      >
                        {downloading === inv.id ? <Loader2 className="animate-spin" size={13} /> : <Download size={13} />} Download Invoice
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}
    </AccountShell>
  );
}
